import { formatCurrency } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, Inject, Input, LOCALE_ID, OnDestroy, signal, ViewChild } from "@angular/core";
import { ApModuleControlService } from "@tylertech/accounts-payable-module";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartBindingObject, ChartDialogService, ChartManagerService, ChartRegistryService, ChartService } from "@tylertech/charting-module";
import { Command, IconCommand } from "@tylertech/commands-module";
import { FileService, LoggingService } from "@tylertech/core-module";
import { ICustomViewComponent } from "@tylertech/custom-view";
import { IChartCriteria } from "@tylertech/tyler-api-types";
import { ChartConfiguration, TooltipItem } from "chart.js";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    selector: "inventory-items-by-class-chart",
    templateUrl: "./inventoryItemsByClassChart.tmpl.html",
    standalone: false
})
export class ItemsByClassChartComponent implements OnDestroy, ICustomViewComponent<ChartBindingObject>
{
    private readonly destructor$ = new Subject<void>();
    readonly commands: ReadonlyArray<Command>;
    private readonly _chartId: string;

    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;
    readonly showChart = signal(false);
    chartConfig: ChartConfiguration<"pie">;

    private _chartCriteria: IChartCriteria;
    readonly customDataBinding: ChartBindingObject;
    private apModuleControl: Record<string, boolean>;
    isLoaded: boolean;

    constructor(
        $chartManagerService: ChartManagerService,
        private readonly $chartDialogService: ChartDialogService,
        private readonly $chartRegistryService: ChartRegistryService,
        private readonly $chartService: ChartService,
        private readonly $loggingService: LoggingService,
        private readonly $fileService: FileService,
        private readonly $apModuleControlService: ApModuleControlService,
        @Inject(LOCALE_ID) private readonly locale: string
    )
    {
        $chartManagerService.refreshChartObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe((chartId: string) =>
            {
                if (chartId !== this.chartId)
                {
                    return;
                }
                this.loadChartData();
            });

        this.commands = [
            new IconCommand({
                icon: "share",
                label: null,
                action: () => this.shareChart(),
                getEnabled: () => this.hasData(),
                tooltip: `Share ${this.ChartTitle}`,
            }),
        ];
    }

    get chartId(): string | undefined
    {
        return this._chartId || this.customDataBinding?.chartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria || this.customDataBinding?.chartCriteria;
    }

    set chartCriteria(value: IChartCriteria)
    {
        this._chartCriteria = value;
    }

    get chartHasData(): boolean
    {
        return this.hasData();
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    get ChartTitle(): string
    {
        if (!this.isLoaded)
        {
            return "Items By Class";
        }
        const setting = this.chartCriteria?.Parameters?.find(v => v.Id === "ItemsByClassChartType");
        const type = setting?.Options?.find(t => t.Value === setting?.Value)?.Display;
        if (!this.apModuleControl?.WHInventoryCategory)
        {
            return type + " Items By Class";
        }
        return type + " Items By Category";
    }

    get ChartSubtitle(): string
    {
        const from = this.chartCriteria?.Parameters?.find(v => v.Id === "ItemsByClassChartFromDate")?.Value || "";
        const to = this.chartCriteria?.Parameters?.find(v => v.Id === "ItemsByClassChartToDate")?.Value || "";
        const sub = `${from} - ${to}`;
        return sub === " - " ? "" : sub;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        this.fetchModuleControl()
            .then(
                () =>
                {
                    if (!this.hasData())
                    {
                        return this.loadChartData();
                    }
                    this.updateChartConfiguration();
                },
                e =>
                {
                    console.error("Error on load", e);
                }
            )
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    private fetchModuleControl(): Promise<void>
    {
        return this.$apModuleControlService.GetSettings()
            .then((result: Record<string, boolean>) =>
            {
                this.apModuleControl = result;
            });
    }

    hasData(): boolean
    {
        return (
            this.chartCriteria &&
            this.chartCriteria.ChartDatas &&
            this.chartCriteria.ChartDatas[0].Data[0] &&
            this.chartCriteria.ChartDatas[0].Data[0].length > 0
        );
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService.GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                    if (!this.hasData())
                    {
                        return;
                    }

                    this.updateChartConfiguration();
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                }
            )
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    openSettings(): void
    {
        this.$chartDialogService.showChartParametersDialog(this.ChartTitle, this.chartCriteria)
            .then((criteria: IChartCriteria) =>
            {
                this.chartCriteria = criteria;
                this.loadChartData();
            });
    }

    private shareChart(): void
    {
        const img = this.chartContainer.createImage();
        this.$fileService.openDocumentLink(img, "image/png", this.ChartTitle.replace(/ /g, ""));
    }

    private updateChartConfiguration(): void
    {
        this.chartConfig = {
            type: "pie",
            options: {
                plugins: {
                    legend: {
                        display: true,
                        position: "right",
                        labels: {
                            color: "#333333"
                        }
                    },
                    tooltip: {
                        callbacks: {
                            title: (tooltipItems: Array<TooltipItem<"pie">>) =>
                            {
                                return this.chartCriteria.ChartDatas[0].Labels[tooltipItems[0].dataIndex];
                            },
                            label: (tooltipItem: TooltipItem<"pie">): string | string[] =>
                            {
                                if (!this.apModuleControl?.WHInventoryCategory)
                                {
                                    return formatCurrency(Number(tooltipItem.raw), this.locale, "$");
                                }
                                return tooltipItem.label;
                            },
                        },
                        backgroundColor: '#ffffff',
                        borderColor: '#767676',
                        borderWidth: 1
                    },
                },
                maintainAspectRatio: true,
            },
            data: {
                labels: this.chartCriteria.ChartDatas[0].Labels,
                datasets: [
                    {
                        data: this.chartCriteria.ChartDatas[0].Data[0],
                        backgroundColor: this.$chartService.getColors(this.chartCriteria.ChartDatas[0].Data[0].length),
                    },
                ],
            },
        };

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }
}
